import { Box, Button, Grid, Toolbar, Typography, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useUtils } from 'utils/dateUtils';
import clsx from 'clsx';

import { ClockView } from '../../components/ClockView/ClockView';

// MUI X v6 toolbar slot props (with legacy aliases kept for safety)
type ToolbarComponentProps = {
  value?: any;
  date?: any;
  onChange: (date: any) => void;
  onViewChange?: (view: string) => void;
  setOpenView?: (view: string) => void;
  isLandscape?: boolean;
  ampm?: boolean;
  hidden?: boolean;
  className?: string;
};

/**
 * Style extracted from original date time picker
 * @link https://github.com/mui-org/material-ui-pickers/blob/next/lib/src/DateTimePicker/DateTimePickerToolbar.tsx
 */
export const useStyles = makeStyles<Theme>(theme => {
  const toolbarBackground =
    theme.palette.mode === 'light' ? theme.palette.primary.main : theme.palette.background.default;
  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      justifyContent: 'space-between',
      width: '100%',
      boxSizing: 'border-box',
      padding: '16px 10px',
      backgroundColor: toolbarBackground,
      color: theme.palette.getContrastText(toolbarBackground),
    },
    dateTitleContainer: {
      flex: 1,
      width: '100%',
      '& button': {
        textTransform: 'none',
        minWidth: '40px',
        padding: '0px 3px',
        textAlign: 'left',

        '& > span': {
          justifyContent: 'flex-start',
        },
      },
    },
  };
});

export const EditableDateTimePickerToolbar = ({
  value,
  date: legacyDate,
  onChange,
  onViewChange,
  setOpenView,
  ampm,
  hidden,
  className,
}: ToolbarComponentProps) => {
  const utils = useUtils();
  const classes = useStyles();

  if (hidden) {
    return null;
  }

  const date = value ?? legacyDate;
  const changeView = onViewChange ?? setOpenView;
  const year = utils.format(date, utils.yearFormat);
  const formattedDate = utils.getDateTimePickerHeaderText(date);

  return (
    <Toolbar data-mui-test="picker-toolbar" className={clsx(classes.root, className)}>
      <Grid
        container
        justifyContent="space-between"
        className={classes.dateTitleContainer}
        direction="row"
        alignItems="flex-end"
      >
        <Grid item>
          <Grid item>
            <Button color="inherit" onClick={() => changeView?.('year')}>
              <Typography variant="subtitle1" align="left">
                {year}
              </Typography>
            </Button>
          </Grid>
          <Grid>
            <Button color="inherit" onClick={() => changeView?.('month')}>
              <Typography variant="h4">{formattedDate}</Typography>
            </Button>
          </Grid>
        </Grid>

        <Box display="flex" flexDirection="row">
          <ClockView date={date} onChange={onChange} ampm={ampm} />
        </Box>
      </Grid>
    </Toolbar>
  );
};
